// BDSJEA-441/443: «Resumen por pasos» del Historial y de la pantalla de resultado.
// Funciones PURAS (sin React) que derivan, del estado de una corrida
// (GET /corridas/{c}/estado), una fila por nivel de resumen con su estado,
// duración y conteos por categoría cerrada. Contrato 7.3 / 7.4.
//
// «No ejecutado» NUNCA lo persiste el migrador: se deriva acá, y solo en
// corridas por niveles (con `parametros.niveles`), para el nivel que no tuvo
// ningún paso en `pasos[]`. Una corrida histórica (sin `parametros.niveles`)
// muestra solo los niveles que tienen paso: nunca «No ejecutado» ni filas de
// actividades que no se ejecutaron.
import { instanteDe } from "./migracionEstadoHelpers"

export const NO_EJECUTADO = "NO_EJECUTADO"

// Nivel de resumen (pantalla): ARCA no es un nivel de selección.
export const NIVELES_RESUMEN = Object.freeze([
  "productores",
  "establecimientos",
  "arca",
  "siembras",
  "ganaderia",
  "tambo",
  // BDSJEA-454: etapa final opcional (paso recalcular_scoring).
  "scoring",
])

export const ETIQUETAS_RESUMEN = Object.freeze({
  productores: "Productores",
  establecimientos: "Establecimientos",
  arca: "ARCA",
  siembras: "Siembras",
  ganaderia: "Ganadería",
  tambo: "Tambo",
  scoring: "Scoring",
})

// Pasos de cada nivel (contrato 7.3).
export const PASOS_DE_NIVEL = Object.freeze({
  productores: ["limpiar_productores", "importar_productores"],
  establecimientos: [
    "limpiar_establecimientos",
    "importar_establecimientos",
    "crear_establecimientos_sin_detalle",
  ],
  arca: ["sincronizar_arca"],
  // BDSJEA-465: el nivel «siembras» importa tambien el stock agricola (las
  // columnas s_* de plan-productivo son QQ de stock): paso extra, sin nivel nuevo.
  siembras: [
    "limpiar_siembras",
    "importar_siembras",
    "importar_stock_agricola",
  ],
  ganaderia: ["limpiar_ganaderia", "importar_ganaderia"],
  tambo: ["limpiar_tambo", "importar_tambo"],
  scoring: ["recalcular_scoring"],
})

// Sub-etapa «Limpieza» de la limpieza completa de la base.
export const PASO_LIMPIEZA_COMPLETA = "limpiar_base_completa"

// De peor a mejor: el estado de un nivel es el peor de sus pasos.
const GRAVEDAD = ["ERROR", "EN_CURSO", "PENDIENTE", "OMITIDO", "OK"]

const peorEstado = estados => {
  let mejor = null
  let rango = Infinity
  estados.forEach(e => {
    const i = GRAVEDAD.indexOf(e)
    // Un estado desconocido pesa como PENDIENTE: ante la duda no se da por OK.
    const r = i === -1 ? GRAVEDAD.indexOf("PENDIENTE") : i
    if (r < rango) {
      rango = r
      mejor = e
    }
  })
  return mejor
}

const lista = v => (Array.isArray(v) ? v : [])
const esNumero = n => typeof n === "number" && Number.isFinite(n)

/** Duración (segundos) = último `fin` − primer `inicio`; null si falta alguno. */
export const duracionDePasos = pasos => {
  const inicios = pasos.map(p => instanteDe(p?.inicio)).filter(ms => ms != null)
  const fines = pasos.map(p => instanteDe(p?.fin)).filter(ms => ms != null)
  if (inicios.length === 0 || fines.length === 0) return null
  const seg = (Math.max(...fines) - Math.min(...inicios)) / 1000
  return seg >= 0 ? Math.round(seg) : null
}

// Conteos por nivel: [etiqueta, campo de `contadores`]. Solo categorías
// cerradas; nunca se leen filas (contrato 7.4).
const CONTEOS = Object.freeze({
  productores: [
    ["Creados", "creados"],
    ["Actualizados", "actualizados"],
    ["Ya existían", "yaExistian"],
    ["Excluidos", "excluidos"],
    ["Descartados", "descartados"],
    ["Errores", "errores"],
  ],
  establecimientos: [
    ["Creados", "creados"],
    ["Ya existían", "saltados"],
    ["Grupos unificados", "gruposLimpios"],
    ["Grupos a revisión", "gruposNoUnificados"],
    ["Descartados", "descartados"],
    ["Errores", "errores"],
    // BDSJEA-471: polígonos aproximados de lotes (aditivos; solo se muestran si > 0)
    ["Polígonos aproximados creados", "poligonosAproximados"],
    ["Lotes sin coordenada", "lotesSinCoordenada"],
    // BDSJEA-470: nivel de rendimiento (plan-establecimiento.zona) de los establecimientos creados (aditivos; solo se muestran si > 0)
    ["Con nivel de rendimiento", "nivelesRendimiento"],
    ["Sin nivel de rendimiento", "sinNivelRendimiento"],
  ],
  arca: [
    ["Sincronizados", "sincronizados"],
    ["Sin datos", "sinDatos"],
    ["Errores", "errores"],
  ],
  siembras: [
    ["Creadas", "creados"],
    ["Actualizadas", "actualizados"],
    ["Ya existían", "saltados"],
    ["Descartadas", "descartados"],
    ["Errores", "errores"],
    ["Avisos", "avisos"],
    ["Sin rendimiento", "sinRendimiento"],
    ["Campañas creadas", "campanasCreadas"],
    ["Sin detalle creados", "sinDetalleCreados"],
  ],
  ganaderia: [
    ["Creados", "creados"],
    ["Ya existían", "yaExistian"],
    ["Descartados", "descartados"],
    ["Errores", "errores"],
    ["Filas de stock", "filasStock"],
    ["Cabezas", "cabezas"],
    ["Avisos", "avisos"],
    ["Sin detalle creados", "sinDetalleCreados"],
  ],
  tambo: [
    ["Tambos activos", "tambosActivos"],
    ["Tambos en historia", "tambosHistoria"],
    ["Ya existían", "saltados"],
    ["Descartados", "descartados"],
    ["Sin datos", "sinDatos"],
    ["Errores", "errores"],
    ["Litros sin dato", "litrosSinDato"],
    ["Avisos", "avisos"],
  ],
  // Los fallidos del paso son su `errores` (contrato 14.4).
  scoring: [
    ["Scorings calculados", "scoringsCalculados"],
    ["Fallidos", "errores"],
  ],
})

// BDSJEA-465: conteos del paso de stock agrícola (nivel «siembras»).
const CONTEOS_STOCK_AGRICOLA = Object.freeze([
  ["Stock agrícola creado", "creados"],
  ["Stock agrícola descartado", "descartados"],
  ["Stock agrícola con error", "errores"],
])

// Paso del que salen los conteos de cada nivel.
const PASO_DE_CONTEOS = Object.freeze({
  productores: "importar_productores",
  establecimientos: "importar_establecimientos",
  arca: "sincronizar_arca",
  siembras: "importar_siembras",
  ganaderia: "importar_ganaderia",
  tambo: "importar_tambo",
  scoring: "recalcular_scoring",
})

/** Conteos [{etiqueta, valor}] > 0 del paso de importación del nivel. */
export const conteosDeNivel = (nivel, pasos) => {
  const paso = lista(pasos).find(p => p?.id === PASO_DE_CONTEOS[nivel])
  const c = paso?.contadores
  const salida = []
  if (c && typeof c === "object") {
    CONTEOS[nivel].forEach(([etiqueta, campo]) => {
      if (esNumero(c[campo]) && c[campo] > 0) {
        salida.push({ etiqueta, valor: c[campo] })
      }
    })
  }
  // BDSJEA-454: un recálculo cortado deja productores «Sin calcular»: lo que
  // falta del total del paso (los fallidos ya cuentan como procesados).
  if (nivel === "scoring" && paso?.estado === "ERROR") {
    const pendientes =
      esNumero(paso.filasTotal) && esNumero(paso.filasProcesadas)
        ? paso.filasTotal - paso.filasProcesadas
        : 0
    if (pendientes > 0) {
      salida.push({ etiqueta: "Sin calcular", valor: pendientes })
    }
  }
  // BDSJEA-465: stock agrícola, paso extra del nivel «siembras».
  if (nivel === "siembras") {
    const stock = lista(pasos).find(p => p?.id === "importar_stock_agricola")
    const c2 = stock?.contadores
    if (c2 && typeof c2 === "object") {
      CONTEOS_STOCK_AGRICOLA.forEach(([etiqueta, campo]) => {
        if (esNumero(c2[campo]) && c2[campo] > 0) {
          salida.push({ etiqueta, valor: c2[campo] })
        }
      })
    }
  }
  // «Sin Detalle» creados por el paso propio de establecimientos.
  if (nivel === "establecimientos") {
    const sd = lista(pasos).find(
      p => p?.id === "crear_establecimientos_sin_detalle",
    )
    const n = sd?.contadores?.creados
    if (esNumero(n) && n > 0) {
      salida.push({ etiqueta: "Sin detalle creados", valor: n })
    }
  }
  return salida
}

/** ¿La corrida se lanzó por niveles? (`parametros.niveles` es una lista). */
export const esCorridaPorNiveles = estado =>
  Array.isArray(estado?.parametros?.niveles)

/**
 * Filas del «Resumen por pasos»: una por nivel de resumen.
 * @param {object|null} estado respuesta de GET /corridas/{c}/estado
 * @returns {Array<{nivel, etiqueta, estado, duracionSeg, pasos, conteos,
 *   motivoOmision}>} vacío si no hay estado o no trae pasos. Una fila extra
 *   `nivel: "limpieza"` si hubo limpieza completa de la base.
 */
export const derivarResumenPorPasos = estado => {
  const pasos = lista(estado?.pasos).filter(p => p && typeof p === "object")
  if (!estado || typeof estado !== "object" || pasos.length === 0) return []
  const porNiveles = esCorridaPorNiveles(estado)
  const filas = []

  const limpieza = pasos.find(p => p.id === PASO_LIMPIEZA_COMPLETA)
  if (limpieza) {
    filas.push({
      nivel: "limpieza",
      etiqueta: "Limpieza completa de la base",
      estado: limpieza.estado || "PENDIENTE",
      duracionSeg: duracionDePasos([limpieza]),
      pasos: [limpieza.id],
      conteos: [],
      motivoOmision: null,
    })
  }

  NIVELES_RESUMEN.forEach(nivel => {
    const propios = pasos.filter(p => PASOS_DE_NIVEL[nivel].includes(p.id))
    if (propios.length === 0) {
      if (porNiveles) {
        filas.push({
          nivel,
          etiqueta: ETIQUETAS_RESUMEN[nivel],
          estado: NO_EJECUTADO,
          duracionSeg: null,
          pasos: [],
          conteos: [],
          motivoOmision: null,
        })
      }
      return
    }
    const estadoNivel = peorEstado(propios.map(p => p.estado))
    // Motivo de una omisión deliberada (p. ej. ARCA con «Excluir ARCA»).
    const conMotivo = propios.find(
      p => typeof p.motivoOmision === "string" && p.motivoOmision.trim(),
    )
    filas.push({
      nivel,
      etiqueta: ETIQUETAS_RESUMEN[nivel],
      estado: estadoNivel,
      duracionSeg: duracionDePasos(propios),
      pasos: propios.map(p => p.id),
      conteos: conteosDeNivel(nivel, pasos),
      motivoOmision: conMotivo ? conMotivo.motivoOmision : null,
    })
  })
  return filas
}

/**
 * ¿Se muestra la sección de una actividad en el Historial? (contrato 9.6)
 * - con estado: si el nivel tiene paso o, en una corrida por niveles, es
 *   «No ejecutado»; en una corrida histórica sin paso, se oculta;
 * - sin estado (no se pudo leer): solo si hay filas.
 * Devuelve null (oculta) o { noEjecutado: boolean }.
 */
export const visibilidadSeccionActividad = (
  nivel,
  filasResumen,
  filasDatos,
) => {
  // BDSJEA-468: el stock agrícola es un paso extra DENTRO del nivel «siembras» (sin fila propia
  // en el resumen por pasos): la sección aparece solo si la corrida trae filas de stock (los
  // historiales anteriores no las traen: sección ausente, sin error).
  if (nivel === "stockAgricola")
    return lista(filasDatos).length > 0 ? { noEjecutado: false } : null
  if (Array.isArray(filasResumen) && filasResumen.length > 0) {
    const fila = filasResumen.find(f => f.nivel === nivel)
    if (!fila) return null
    return { noEjecutado: fila.estado === NO_EJECUTADO }
  }
  return lista(filasDatos).length > 0 ? { noEjecutado: false } : null
}
